import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import api from '../api/api';

/**
 * Компактный TOTP-код для сотрудника в таблице заявок.
 * Сам тикает и обновляется. Доступ сотрудника проверяется отдельно от клиентского кабинета.
 */
export default function EmployeePickupCode({ token }) {
  const [code, setCode] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    let stopped = false;
    let inFlight = false;
    let remaining = 0;
    setCode(null);
    setError(null);
    setSecondsLeft(0);
    if (!token) return;
    const fetchCode = async () => {
      if (!alive || stopped || inFlight) return;
      inFlight = true;
      try {
        const res = await api.get(`/client-portal/admin/order/${token}/pickup-code`);
        if (!alive) return;
        const seconds = Number(res.data.seconds_left);
        if (!res.data.code || !Number.isFinite(seconds) || seconds < 0) throw new Error('Invalid pickup code');
        setCode(res.data.code);
        remaining = Math.floor(seconds);
        setSecondsLeft(remaining);
        setError(null);
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        setError(status === 410 ? 'Использован' : status === 403 ? 'Не оплачено' : status === 404 ? '—' : 'Ошибка');
        stopped = true;
      } finally {
        inFlight = false;
      }
    };
    fetchCode();
    const interval = setInterval(() => {
      if (stopped) return;
      remaining = Math.max(0, remaining - 1);
      setSecondsLeft(remaining);
      if (remaining === 0) fetchCode();
    }, 1000);
    return () => { alive = false; clearInterval(interval); };
  }, [token]);

  if (error) {
    return <Typography variant="caption" color="text.secondary">{error}</Typography>;
  }

  if (!code) {
    return <Typography variant="caption" color="text.secondary">…</Typography>;
  }

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const lowTime = secondsLeft <= 30;

  return (
    <Tooltip title="Динамический код. Обновляется каждые 5 минут.">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: lowTime ? 'warning.main' : 'primary.main',
            letterSpacing: 1,
            lineHeight: 1.1,
          }}
        >
          {code}
        </Typography>
        <Typography variant="caption" color={lowTime ? 'warning.main' : 'text.secondary'} sx={{ fontSize: 10 }}>
          {mins}:{String(secs).padStart(2, '0')}
        </Typography>
      </Box>
    </Tooltip>
  );
}
